/**
 * Stage 2d: Competitor Aggregate — context-based version using StagePanel.
 *
 * Synthesizes all competitor extractions into strategic intelligence.
 * LLM stage — reads SERP overview + extractions, produces analysis.
 */

import React from 'react';
import { StagePanel } from '../StagePanel';
import { useArticleContext } from '../ArticleContext';
import type { ArticleDocument } from '../types';

function InputSummary() {
  const { document } = useArticleContext();
  const serp = document?.targeting?.serp;
  const extractions = document?.targeting?.competitors?.extractions || [];

  return (
    <div className="space-y-3">
      <div>
        <div className="text-xs text-gray-500 mb-1">SERP overview (from 2a)</div>
        <textarea
          className="w-full border rounded px-2 py-1 text-xs bg-gray-50"
          rows={4}
          value={serp?.overview
            ? JSON.stringify(serp.overview, null, 2)
            : 'No SERP data available.'}
          disabled
        />
      </div>
      <div>
        <div className="text-xs text-gray-500 mb-1">Competitor extractions (from 2c) — {extractions.length} competitors</div>
        <textarea
          className="w-full border rounded px-2 py-1 text-xs bg-gray-50"
          rows={4}
          value={extractions.length > 0
            ? JSON.stringify(extractions.map(e => ({ domain: e.domain, page_title: e.page_title })), null, 2)
            : 'No extractions available.'}
          disabled
        />
      </div>
    </div>
  );
}

function formatItem(item: unknown): string {
  if (typeof item === 'string') return item;
  if (item && typeof item === 'object') {
    const obj = item as Record<string, unknown>;
    if (typeof obj.gap === 'string') return obj.gap;
    return JSON.stringify(item);
  }
  return String(item);
}

function renderOutput(doc: ArticleDocument) {
  const analysis = doc.targeting?.competitors?.analysis;
  if (!analysis) {
    return <div className="text-sm text-gray-500">No analysis yet.</div>;
  }
  return (
    <div className="space-y-3">
      {analysis.table_stakes.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-700 mb-1">Table stakes</div>
          <ul className="text-xs text-gray-600 list-disc pl-4">
            {analysis.table_stakes.map((item, i) => <li key={i}>{formatItem(item)}</li>)}
          </ul>
        </div>
      )}
      {analysis.content_gaps.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-700 mb-1">Content gaps</div>
          <ul className="text-xs text-gray-600 list-disc pl-4">
            {analysis.content_gaps.map((item, i) => {
              const obj = item && typeof item === 'object' ? item as Record<string, unknown> : null;
              const priority = obj?.priority as string | undefined;
              return (
                <li key={i}>
                  {priority && (
                    <span className={`inline-block text-[10px] font-medium mr-1.5 px-1 rounded ${
                      priority === 'high' ? 'bg-red-100 text-red-700' :
                      priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>{priority}</span>
                  )}
                  {formatItem(item)}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {analysis.anti_patterns.length > 0 && (
        <div>
          <div className="text-xs font-medium text-gray-700 mb-1">Anti-patterns</div>
          <ul className="text-xs text-gray-600 list-disc pl-4">
            {analysis.anti_patterns.map((item, i) => <li key={i}>{formatItem(item)}</li>)}
          </ul>
        </div>
      )}
      {analysis.differentiation_angle && (
        <div>
          <div className="text-xs font-medium text-gray-700 mb-1">Differentiation angle</div>
          <div className="text-xs text-gray-600">{analysis.differentiation_angle}</div>
        </div>
      )}
      {analysis.target_word_count != null && (
        <div>
          <div className="text-xs font-medium text-gray-700 mb-1">Target word count</div>
          <div className="text-xs text-gray-600">{analysis.target_word_count} — {analysis.word_count_rationale || ''}</div>
        </div>
      )}
    </div>
  );
}

export function Stage2dAggregateV2() {
  const { document } = useArticleContext();
  const hasExtractions = (document?.targeting?.competitors?.extractions || []).length > 0;

  return (
    <StagePanel
      stageId="stage_2d_competitor_aggregate"
      isLlmStage
      title="Stage 2d: Competitor Aggregate"
      description="Synthesizes competitor extractions into table stakes, gaps, and differentiation."
      runDisabled={!hasExtractions}
      runDisabledReason={!hasExtractions ? 'Requires Stage 2c competitor extractions' : undefined}
      renderOutput={renderOutput}
    >
      <InputSummary />
    </StagePanel>
  );
}
